import React from 'react';
import {useIntl} from 'react-intl';
import moment from 'moment';
import {useExtendedDateRangeContext} from '../../ExtendedDateRangeContext';
import {Chart} from './Chart';
import Unit from '../../../_common/components/Unit';
import {getOptionsBar, getOptionsLine} from '../../constants/chartsProps';
import styles from '../../pages/styles.module.scss';

export const ExtendedDateRange = () => {
  const intl = useIntl();
  const prefix = 'dashboard-page';
  const {dataBar, dataLine} = useExtendedDateRangeContext();

  const getXData = (data) => {
    const result = data ? data.map(item => {
      const trimmedStr = item.replace(/ /g, '');
      const array = trimmedStr.split('/');
      return array.length > 0
        ? array.map((item) => {
            return moment(item).format('DD.MM.YY');
          }
        )
        : [];
    }) : [];

    return result.filter(item => item !== '');
  };
  const getBarsYData = (data, firstLabel, secondLabel) => {
    return data ? data.map(item => {
      const {name, data} = item;
      const hebrewName = name === 'total' ? firstLabel : secondLabel;
      return {data, name: hebrewName};
    }) : [];
  };
  const getCustomTooltipBar = ({series, seriesIndex, dataPointIndex}) => {
    switch (seriesIndex) {
      case 1:
        const percentInTotal = Boolean(series[0][dataPointIndex])
          ? `${(series[1][dataPointIndex] / series[0][dataPointIndex] * 100).toFixed(2)}%`
          : ''
        ;
        return '<div class="bg-danger text-white p-2">'
          + percentInTotal
          + '</div>';
      default:
        return null;
    }
  };

  const tasksStats = dataBar?.tasksStats ? dataBar?.tasksStats : [];
  const priceQuotesStats = dataBar?.priceQuotesStats ? dataBar?.priceQuotesStats : [];
  const ordersStats = dataBar?.ordersStats ? dataBar?.ordersStats : [];
  const inspectionsStats = dataLine?.inspectionsStats ? dataLine?.inspectionsStats : [];
  const xData = inspectionsStats.map(item => item.currentPeriod);
  const averageSqmPrice = inspectionsStats.map(item => item.averageSqmPrice || 0);
  const totalInspectionsCount = inspectionsStats.map(item => item.totalInspectionsCount);

  const getCustomTooltipPriceTime = ({series, seriesIndex, dataPointIndex}) => {
    const glassStyle = `
    background: rgba(255, 255, 255, 0.72);   
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    direction: rtl;
    text-align: right;
  `;

    const rowStyle = `padding: 10px 14px; display: flex; flex-direction: column; gap: 2px;`;
    const labelStyle = `font-size: 11px; text-transform: uppercase; color: #1d1d1f;`;
    const valueStyle = `font-size: 14px; font-weight: 600; color: #1d1d1f;`;

    return `
    <div style="${glassStyle}">
      <div style="${rowStyle} background: rgba(0, 122, 255, 0.1); border-bottom: 1px solid rgba(0, 0, 0, 0.05);">
        <span style="${labelStyle}">${intl.formatMessage({
      id: `${prefix}-graph-tooltip-monthYear`,
      defaultMessage: 'Month/year: '
    })}</span>
        <span style="${valueStyle}">${getXData(xData)[dataPointIndex]}</span>
      </div>

      <div style="${rowStyle} border-bottom: 1px solid rgba(0, 0, 0, 0.05);">
        <span style="${labelStyle}">${intl.formatMessage({
      id: `${prefix}-graph-tooltip-averagePrice`,
      defaultMessage: 'Average price: '
    })}</span>
        <span style="${valueStyle}; color: #007AFF;">${series[seriesIndex][dataPointIndex]}</span>
      </div>

      <div style="${rowStyle}">
        <span style="${labelStyle}">${intl.formatMessage({
      id: `${prefix}-graph-tooltip-count`,
      defaultMessage: 'Count: '
    })}</span>
        <span style="${valueStyle}; color: #FF9500;">${totalInspectionsCount[dataPointIndex]}</span>
      </div>
    </div>
  `;
  };

  const commonProps = {width: '100%', height: '300px'};

  return (
    <>
      <div className={styles.barChartsContainer}>
        <Unit
          text={intl.formatMessage({id: `${prefix}-tasksStatus-header`, defaultMessage: 'Tasks status'})}
          customClassnames={'w-100 mr-4'}
          icon={'/media/common/Chart-bar_primary.svg'}
        >
          <div className={styles.barChart}>
            <Chart
              options={getOptionsBar(
                getXData(tasksStats?.xaxis),
                getBarsYData(tasksStats?.series,
                  intl.formatMessage({id: `${prefix}-chart-bar-label-tasks`, defaultMessage: 'Tasks'}),
                  intl.formatMessage({id: `${prefix}-chart-bar-label-backlogTasks`, defaultMessage: 'Backlog tasks'})
                ),
                getCustomTooltipBar
              )}
              type={'bar'}
              {...commonProps}
            />
          </div>
        </Unit>
        <Unit
          text={intl.formatMessage({id: `${prefix}-priceQuotesStats-header`, defaultMessage: 'Price quotes status'})}
          customClassnames={'w-100'}
          icon={'/media/common/Chart-bar_primary.svg'}
        >
          <div className={styles.barChart}>
            <Chart
              options={getOptionsBar(
                getXData(priceQuotesStats?.xaxis),
                getBarsYData(priceQuotesStats?.series,
                  intl.formatMessage({id: `${prefix}-chart-bar-label-priceQuotes`, defaultMessage: 'Price quotes'}),
                  intl.formatMessage({
                    id: `${prefix}-chart-bar-label-backlogPriceQuotes`,
                    defaultMessage: 'Backlog price quotes'
                  })
                ),
                getCustomTooltipBar
              )}
              type={'bar'}
              {...commonProps}
            />
          </div>
        </Unit>
        <Unit
          text={intl.formatMessage({id: `${prefix}-ordersStats-header`, defaultMessage: 'Orders status'})}
          customClassnames={'w-100 mr-4'}
          icon={'/media/common/Chart-bar_primary.svg'}
        >
          <div className={styles.barChart}>
            <Chart
              options={getOptionsBar(
                getXData(ordersStats?.xaxis),
                getBarsYData(
                  ordersStats?.series,
                  intl.formatMessage({id: `${prefix}-chart-bar-label-orders`, defaultMessage: 'Orders'}),
                  intl.formatMessage({id: `${prefix}-chart-bar-label-backlogOrders`, defaultMessage: 'Backlog orders'})
                ),
                getCustomTooltipBar
              )}
              type={'bar'}
              {...commonProps}
            />
          </div>
        </Unit>
        <Unit
          text={intl.formatMessage({id: `${prefix}-priceTime-header`, defaultMessage: 'Price per m*2 by month'})}
          customClassnames={'w-100'}
          icon={'/media/common/Chart-line_primary.svg'}
        >
          <div className={styles.barChart}>
            <Chart
              options={getOptionsLine(
                getXData(xData),
                averageSqmPrice,
                getCustomTooltipPriceTime
              )}
              type={'line'}
              {...commonProps}
            />
          </div>
        </Unit>
      </div>
    </>
  );
};